import React, { useState, useEffect, useCallback } from 'react';
import { MessageSquare, Filter, Clock, CheckCircle, AlertCircle, Ban, Mail, MailOpen, ArrowUpCircle, ArrowRightCircle } from 'lucide-react';
import { fairManagerInquiryApi, fairManagerApi } from '../services/api';
import type { FairInquiryResponse, InquiryStatus, InquiryPriority, FairInquiryListResponse, Fair } from '../types';
import { useNavigate } from 'react-router-dom';
import { extractApiError } from '../utils/errorUtils';

const FairManagerInquiryDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [inquiries, setInquiries] = useState<FairInquiryResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fair selection (Issue #959)
  const [fairs, setFairs] = useState<Fair[]>([]);
  const [selectedFairId, setSelectedFairId] = useState<number | null>(null);
  const [isFairsLoading, setIsFairsLoading] = useState(true);
  const [fairsError, setFairsError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState<InquiryStatus | 'all'>('all');
  const [priorityFilter, setPriorityFilter] = useState<InquiryPriority | 'all'>('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  // Statistics
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    in_progress: 0,
    waiting_for_vendor: 0,
    resolved: 0,
    closed: 0,
    urgent: 0,
    high: 0
  });

  // Calculate statistics from inquiries
  const calculateStats = useCallback((items: FairInquiryResponse[]) => {
    const newStats = {
      total: items.length,
      new: items.filter(i => i.status === 'new').length,
      in_progress: items.filter(i => i.status === 'in_progress').length,
      waiting_for_vendor: items.filter(i => i.status === 'waiting_for_vendor').length,
      resolved: items.filter(i => i.status === 'resolved').length,
      closed: items.filter(i => i.status === 'closed').length,
      urgent: items.filter(i => i.priority === 'urgent').length,
      high: items.filter(i => i.priority === 'high').length
    };
    setStats(newStats);
  }, []);

  // Load fairs (Issue #959)
  useEffect(() => {
    const loadFairs = async () => {
      setIsFairsLoading(true);
      setFairsError(null);

      try {
        const fairsList = await fairManagerApi.getFairs();
        setFairs(fairsList);

        // Auto-select first fair if available (using functional update to avoid dependency)
        setSelectedFairId(currentId => {
          if (currentId === null && fairsList.length > 0) {
            return fairsList[0].id;
          }
          return currentId;
        });
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to fetch fairs:', apiError);
        setFairsError(apiError.message);
      } finally {
        setIsFairsLoading(false);
      }
    };

    loadFairs();
  }, []);

  // Fetch inquiries (Issue #959: Now requires fairId)
  const fetchInquiries = useCallback(async () => {
    // Don't fetch if no fair is selected
    if (!selectedFairId) {
      setInquiries([]);
      setTotalPages(1);
      setTotal(0);
      calculateStats([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response: FairInquiryListResponse = await fairManagerInquiryApi.getFairInquiries(
        selectedFairId,  // Issue #959: Use selected fair ID instead of undefined
        statusFilter === 'all' ? undefined : statusFilter,
        priorityFilter === 'all' ? undefined : priorityFilter,
        page,
        20  // page size
      );

      setInquiries(response.items);
      setTotalPages(response.total_pages);
      setTotal(response.total);

      // Calculate statistics
      calculateStats(response.items);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to fetch inquiries:', apiError);
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedFairId, statusFilter, priorityFilter, page, calculateStats]);

  // Fetch on mount and when filters change
  useEffect(() => {
    if (!isFairsLoading) {
      fetchInquiries();
    }
  }, [fetchInquiries, isFairsLoading]);

  // Get status badge styling
  const getStatusBadge = (status: InquiryStatus) => {
    const badges = {
      new: {
        icon: Mail,
        className: 'bg-moonbone text-ink',
        label: 'New',
      },
      in_progress: {
        icon: Clock,
        className: 'bg-yellow-100 text-yellow-800',
        label: 'In Progress',
      },
      waiting_for_vendor: {
        icon: AlertCircle,
        className: 'bg-orange-100 text-orange-800',
        label: 'Waiting for Vendor',
      },
      resolved: {
        icon: CheckCircle,
        className: 'bg-green-100 text-green-800',
        label: 'Resolved',
      },
      closed: {
        icon: Ban,
        className: 'bg-gray-100 text-gray-800',
        label: 'Closed',
      },
    };

    const badge = badges[status];
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.className}`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  // Get priority badge styling
  const getPriorityBadge = (priority: InquiryPriority) => {
    const priorities = {
      urgent: { className: 'bg-red-100 text-red-800 border-red-200', label: 'Urgent', icon: ArrowUpCircle },
      high: { className: 'bg-orange-100 text-orange-800 border-orange-200', label: 'High', icon: ArrowRightCircle },
      normal: { className: 'bg-gray-100 text-gray-600 border-gray-200', label: 'Normal', icon: ArrowRightCircle },
      low: { className: 'bg-gray-50 text-gray-500 border-gray-100', label: 'Low', icon: ArrowRightCircle },
    };

    const badge = priorities[priority];
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${badge.className}`}>
        <Icon className="w-3 h-3 mr-1" />
        {badge.label}
      </span>
    );
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Loading state (fairs or inquiries)
  if ((isFairsLoading || isLoading) && inquiries.length === 0 && fairs.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="inquiries-loading">
        <div className="flex items-center space-x-2 text-gray-600">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  // Fair selection error
  if (fairsError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
            <div>
              <h3 className="font-medium text-red-900">Failed to Load Fairs</h3>
              <p className="text-sm text-red-700 mt-1">{fairsError}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No fairs available
  if (fairs.length === 0 && !isFairsLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow p-12 text-center max-w-md">
          <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Fairs Found</h3>
          <p className="text-gray-600">
            You don't have any fairs to manage yet. Create a fair to start managing inquiries.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="inquiries-inbox">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <MessageSquare className="h-8 w-8 text-secondary" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Fair Manager Inquiries</h1>
              <p className="text-sm text-gray-600 mt-1">
                {total} {total === 1 ? 'inquiry' : 'inquiries'} total
              </p>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6" data-testid="inquiry-stats">
          <div className="bg-moonbone border border-teal-200 rounded-lg p-4" data-testid="stats-new">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-secondary font-medium">New</p>
                <p className="text-2xl font-bold text-ink">{stats.new}</p>
              </div>
              <Mail className="h-8 w-8 text-secondary" />
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-testid="stats-in-progress">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600 font-medium">In Progress</p>
                <p className="text-2xl font-bold text-yellow-900">{stats.in_progress}</p>
              </div>
              <Clock className="h-8 w-8 text-yellow-600" />
            </div>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4" data-testid="stats-waiting-vendor">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600 font-medium">Awaiting Vendor</p>
                <p className="text-2xl font-bold text-orange-900">{stats.waiting_for_vendor}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-orange-600" />
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="stats-high-priority">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium">High Priority</p>
                <p className="text-2xl font-bold text-red-900">{stats.urgent + stats.high}</p>
              </div>
              <ArrowUpCircle className="h-8 w-8 text-red-600" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6" data-testid="inquiry-filters">
          <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-4 md:space-y-0">
            <Filter className="h-5 w-5 text-gray-400" />

            {/* Fair Selector (Issue #959) */}
            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Fair:</label>
              <select
                data-testid="inquiry-fair-select"
                value={selectedFairId ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  const fairId = value === '' ? null : parseInt(value, 10);
                  setSelectedFairId(fairId);
                  setPage(1);  // Reset to first page
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-primary focus:border-primary min-w-[200px]"
              >
                {fairs.map((fair) => (
                  <option key={fair.id} value={fair.id}>
                    {fair.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Status:</label>
              <select
                data-testid="inquiry-status-select"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value as InquiryStatus | 'all');
                  setPage(1);  // Reset to first page
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-primary focus:border-primary"
              >
                <option value="all">All</option>
                <option value="new">New</option>
                <option value="in_progress">In Progress</option>
                <option value="waiting_for_vendor">Waiting for Vendor</option>
                <option value="resolved">Resolved</option>
                <option value="closed">Closed</option>
              </select>
            </div>

            <div className="flex items-center space-x-2">
              <label className="text-sm font-medium text-gray-700">Priority:</label>
              <select
                data-testid="inquiry-priority-select"
                value={priorityFilter}
                onChange={(e) => {
                  setPriorityFilter(e.target.value as InquiryPriority | 'all');
                  setPage(1);  // Reset to first page
                }}
                className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-primary focus:border-primary"
              >
                <option value="all">All</option>
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="normal">Normal</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6" data-testid="inquiry-error-message">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-red-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-red-900">Error</h3>
                <p className="text-sm text-red-700 mt-1">{error}</p>
                <button
                  data-testid="retry-inquiries-button"
                  onClick={fetchInquiries}
                  className="mt-2 text-sm font-medium text-red-600 hover:text-red-700"
                >
                  Try again
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Inquiries List */}
        {inquiries.length === 0 && !isLoading ? (
          <div className="bg-white rounded-lg shadow p-12 text-center" data-testid="no-inquiries-message">
            <MessageSquare className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No inquiries found</h3>
            <p className="text-gray-600 mb-6">
              {statusFilter === 'all' && priorityFilter === 'all'
                ? "No inquiries have been submitted yet."
                : `No inquiries match the selected filters.`}
            </p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="inquiry-list">
            {inquiries.map((inquiry) => (
              <div
                key={inquiry.id}
                data-testid="inquiry-item"
                onClick={() => navigate(`/fairmanager/inquiries/${inquiry.id}`)}
                className="bg-white rounded-lg shadow hover:shadow-md transition-shadow cursor-pointer p-6 border-l-4 border-primary"
              >
                <div className="flex items-start justify-between">
                  {/* Left: Inquiry details */}
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      {inquiry.unread_count > 0 ? (
                        <Mail className="h-5 w-5 text-secondary" />
                      ) : (
                        <MailOpen className="h-5 w-5 text-gray-400" />
                      )}
                      <h3 className="text-lg font-medium text-gray-900">{inquiry.subject}</h3>
                    </div>

                    <div className="flex items-center space-x-4 mb-3">
                      <span className="text-sm text-gray-600">
                        <strong>Vendor:</strong> {inquiry.vendor_name}
                      </span>
                      <span className="text-sm text-gray-600">
                        <strong>Fair:</strong> {inquiry.fair_name}
                      </span>
                      <span className="text-sm text-gray-600">
                        <strong>Type:</strong> {inquiry.inquiry_type.replace('_', ' ')}
                      </span>
                    </div>

                    <div className="flex items-center space-x-3">
                      <span data-testid="inquiry-status-badge">{getStatusBadge(inquiry.status)}</span>
                      <span data-testid="inquiry-priority-badge">{getPriorityBadge(inquiry.priority)}</span>
                      {inquiry.unread_count > 0 && (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-moonbone text-ink" data-testid="inquiry-unread-badge">
                          {inquiry.unread_count} unread
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Right: Timestamp */}
                  <div className="text-sm text-gray-500 text-right">
                    <div>{formatDate(inquiry.last_message_at || inquiry.created_at)}</div>
                    {inquiry.last_message_at && (
                      <div className="text-xs mt-1">Last message</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-between" data-testid="inquiry-pagination">
            <button
              data-testid="inquiry-pagination-previous"
              onClick={() => setPage(page - 1)}
              disabled={page === 1}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            <span className="text-sm text-gray-700" data-testid="inquiry-pagination-info">
              Page {page} of {totalPages}
            </span>

            <button
              data-testid="inquiry-pagination-next"
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default FairManagerInquiryDashboard;
